import React from "react";

import { Trash2 as BinIcon } from "react-feather";
import { Box, makeStyles } from "@material-ui/core";

import {
  AreaDiscounts,
  AreaPricesCard,
  DeleteAreaConfirmationModal,
  DistrictCoverage,
  FormPageActions,
  Page,
} from "@hubcapp/components";
import { ProductCategoryType } from "@hubcapp/types";
import Button from "@ds-developer/button";
import Content from "@ds-developer/content";
import PageHeader from "@ds-developer/page-header";

import { ManageAreaProps } from "./types";
import { getFranchiseLabel } from "../helpers";

const useStyles = makeStyles(theme => ({
  actionButtons: {
    display: "flex",
    flexDirection: "column-reverse",
    alignItems: "center",
    [theme.breakpoints.up("md")]: {
      flexDirection: "row",
    },
  },
  deleteButton: {
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up("sm")]: {
      margin: theme.spacing(0, 4, 0, 0),
    },
  },
}));

const ManageArea: React.FC<ManageAreaProps> = ({
  id,
  name,
  areaCode,
  pricing,
  type,
  discounts,
  onDiscountsChange,
  districts,
  canEditDistricts,
  canEditPrices,
  onDistrictsChange,
  onPriceChange,
  isDeleteModalOpen = false,
  onDeleteClick = () => {},
  onDeleteCancel = () => {},
  onDeleteCompleted = () => {},
  onDiscard = () => {},
  onSubmit = () => {},
  onDelete = () => {},
  dirty = false,
  loading,
  proposition,
  propositionId = "",
  productCategoryId = "",
  submitting = false,
  priceError = false,
}) => {
  const classes = useStyles();

  return (
    <Page title={areaCode ? `${areaCode} ${name} | Coverage` : "Coverage area"}>
      <Content>
        <PageHeader
          title={
            !loading &&
            `Edit my ${areaCode} ${name} area${
              proposition ? ` for ${getFranchiseLabel(true, proposition)}` : ""
            }`
          }
          breadcrumbs={[
            {
              title: "Home",
              link: "/",
            },
            {
              title: "Areas & prices",
            },
          ]}
        />

        {id && isDeleteModalOpen && (
          <DeleteAreaConfirmationModal
            onCancelClick={onDeleteCancel}
            onCompleted={onDeleteCompleted}
            handleOnDelete={onDelete}
          />
        )}

        <DistrictCoverage
          loading={loading}
          areaId={id}
          areaName={name}
          areaShortCode={areaCode}
          canEdit={canEditDistricts}
          onDistrictsUpdate={onDistrictsChange}
          instructorDistricts={districts}
        />

        {pricing?.length && (
          <Box mt={3}>
            <AreaPricesCard
              loading={loading}
              areaCode={areaCode}
              areaName={name}
              editable={canEditPrices}
              prices={pricing}
              onPriceChange={onPriceChange}
            />
          </Box>
        )}

        {discounts && type === ProductCategoryType.PreTest && (
          <Box mt={3}>
            <AreaDiscounts
              areaId={id}
              instructorDiscounts={discounts}
              loading={loading}
              onChange={onDiscountsChange}
              propositionId={propositionId}
              productCategoryId={productCategoryId}
            />
          </Box>
        )}

        <Box marginY={5} className={classes.actionButtons}>
          <Box className={classes.deleteButton}>
            <Button
              onClick={onDeleteClick}
              variant="contained"
              color="error"
              startIcon={<BinIcon size={14} />}
              loading={loading}
              data-test="delete-button"
            >
              Delete this area
            </Button>
          </Box>

          <FormPageActions
            onDiscardClick={onDiscard}
            onSaveClick={onSubmit}
            submitDisabled={!dirty || submitting || priceError}
          />
        </Box>
      </Content>
    </Page>
  );
};

export default ManageArea;
